import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { toast } from '../components/ui/Toast.js';
import { useAuthStore } from '../stores/auth-store.js';
import { api, ApiError } from '../lib/api.js';
import type { CreateRoomResponse } from '@ember/types';

export default function HomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [isCreating, setIsCreating] = useState(false);
  const [joinSlug, setJoinSlug] = useState('');
  const [joinError, setJoinError] = useState('');

  async function handleCreateRoom() {
    setIsCreating(true);
    try {
      const { room } = await api.post<CreateRoomResponse>('/api/rooms');
      toast.success('Room created!');
      navigate(`/r/${room.slug}`);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error('Failed to create room. Please try again.');
      }
    } finally {
      setIsCreating(false);
    }
  }

  function handleJoin() {
    setJoinError('');
    // Accept a full URL or just the slug
    let slug = joinSlug.trim();
    const urlMatch = slug.match(/\/r\/([a-z0-9-]+)/);
    if (urlMatch) slug = urlMatch[1] ?? slug;

    if (!slug) {
      setJoinError('Enter a room link or slug.');
      return;
    }
    navigate(`/r/${slug}`);
  }

  return (
    <div className="flex min-h-screen flex-col bg-neutral-950">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
        <div className="flex items-center gap-2">
          <span className="text-xl">🔥</span>
          <span className="font-bold text-neutral-100">Ember</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-400">{user?.displayName}</span>
          <Button variant="ghost" size="sm" onClick={() => { void logout(); }}>
            Sign out
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8">
          {/* Hero */}
          <div className="text-center">
            <span className="text-5xl">🔥</span>
            <h1 className="mt-4 text-3xl font-bold text-neutral-100">Start or join a call</h1>
            <p className="mt-2 text-neutral-400">
              End-to-end encrypted voice &amp; video — no accounts needed for guests.
            </p>
          </div>

          {/* Create room */}
          <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-6">
            <h2 className="mb-3 text-base font-semibold text-neutral-100">New meeting</h2>
            <p className="mb-4 text-sm text-neutral-400">
              Create a room and share the link. You'll be the host.
            </p>
            <Button onClick={handleCreateRoom} isLoading={isCreating} className="w-full">
              🎙 Start a meeting
            </Button>
          </div>

          {/* Join room */}
          <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-6">
            <h2 className="mb-3 text-base font-semibold text-neutral-100">Join a meeting</h2>
            <div className="flex gap-2">
              <Input
                placeholder="Paste link or slug (e.g. swift-river-42)"
                value={joinSlug}
                onChange={(e) => { setJoinSlug(e.target.value); setJoinError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleJoin(); }}
                error={joinError}
                className="flex-1"
              />
              <Button variant="secondary" onClick={handleJoin}>
                Join
              </Button>
            </div>
          </div>

          {/* E2EE indicator */}
          <p className="text-center text-xs text-neutral-600">
            🔒 All calls are end-to-end encrypted — media never touches our servers
          </p>
        </div>
      </main>
    </div>
  );
}
